import React, { useState } from 'react';
import { Menu, X, Briefcase, MapPin } from 'lucide-react';
import { HalloweekLink } from './HalloweekLink';
const GOOGLE_MAPS_URL = 'https://maps.app.goo.gl/YkZ8cAkm4wWJcYB3A';
export type ServiceType = 'foretagsbestallning' | 'manadens_meny' | 'catering_event';
interface NavigationProps {
  onOrderClick: (type: ServiceType) => void;
  onJobsClick: () => void;
  onBusinessOrder?: () => void;
  onScrollToCatalog?: () => void;
  onEventsClick?: () => void;
  isEventsActive?: boolean;
}
export function Navigation({
  onOrderClick,
  onJobsClick,
  onBusinessOrder,
  onScrollToCatalog,
  onEventsClick,
  isEventsActive = false
}: NavigationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dark = isEventsActive;
  const handleLinkClick = (type: ServiceType) => {
    if (type === 'foretagsbestallning' && onBusinessOrder) {
      onBusinessOrder();
    } else if (type === 'catering_event' && onScrollToCatalog) {
      onScrollToCatalog();
    } else {
      onOrderClick(type);
    }
    setIsOpen(false);
  };
  const handleJobsClick = () => {
    onJobsClick();
    setIsOpen(false);
  };
  const handleEventsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onEventsClick?.();
    setIsOpen(false);
  };
  const links: {
    label: string;
    type: ServiceType;
  }[] = [{
    label: 'Företagsbeställning',
    type: 'foretagsbestallning'
  }, {
    label: 'Catering & Event',
    type: 'catering_event'
  }];
  const t = dark ? {
    nav: 'bg-[#0E0C0B] border-[#F5EDE3]/10',
    link: 'text-[#F5EDE3]/75 hover:text-[#FF9A4D]',
    mobileLink: 'text-[#F5EDE3]/80 hover:bg-[#F5EDE3]/5 hover:text-[#FF9A4D]',
    panel: 'bg-[#0E0C0B] border-[#F5EDE3]/10',
    label: 'text-[#F5EDE3]/40',
    divider: 'bg-[#F5EDE3]/10',
    toggle: 'text-[#F5EDE3]/80 hover:bg-[#F5EDE3]/5'
  } : {
    nav: 'bg-[#F2EDE4] border-[#7C8062]/15',
    link: 'text-[#1A1A1A]/70 hover:text-[#7C8062]',
    mobileLink: 'text-[#1A1A1A]/70 hover:bg-[#7C8062]/10 hover:text-[#7C8062]',
    panel: 'bg-[#F2EDE4] border-[#7C8062]/10',
    label: 'text-[#1A1A1A]/40',
    divider: 'bg-[#7C8062]/10',
    toggle: 'text-[#1A1A1A]/70 hover:bg-[#7C8062]/10'
  };
  return <nav className={`sticky top-0 z-40 w-full border-b shadow-sm transition-colors duration-200 ${t.nav}`} data-id="element-883" data-mp="ze25030uy59w">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-884" data-mp="1d4x4wptdpc1c">
        <div className="flex justify-center items-center h-12" data-id="element-885" data-mp="187x2qpzngtfi">
          {/* Desktop Menu - Centered */}
          <div className="hidden md:flex items-center space-x-8 lg:space-x-10" data-id="element-886" data-mp="1n4xu2sdximdg">
            {links.map(link => <button key={link.type} onClick={() => handleLinkClick(link.type)} className={`flex items-center gap-1.5 text-sm font-medium transition-colors tracking-wide ${t.link}`} data-id="element-887" data-mp="1i6w921wy0czc">
                {link.label}
              </button>)}
            <HalloweekLink isActive={isEventsActive} dark={dark} onClick={handleEventsClick} data-id="element-888" data-mp="1zffa59wdrsbz" />
            <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-1.5 text-sm font-medium transition-colors tracking-wide ${t.link}`} data-id="element-889" data-mp="1ppdqqvez2y3e">
              <MapPin className="w-4 h-4" data-id="element-890" data-mp="1gn4d3woeqols" />
              Öppettider & Hitta hit
            </a>
            <button onClick={handleJobsClick} className={`flex items-center gap-1.5 text-sm font-medium transition-colors tracking-wide ${t.link}`} data-id="element-891" data-mp="1krc5q4xzkopa">
              <Briefcase className="w-4 h-4" data-id="element-892" data-mp="1bpuryt81wz3r" />
              Jobba hos oss
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2" data-id="element-893" data-mp="3633u1zy9pibz">
            <button onClick={() => setIsOpen(!isOpen)} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg focus:outline-none transition-colors ${t.toggle}`} aria-label="Meny" aria-expanded={isOpen} data-id="element-894" data-mp="362klvayf68b5">
              {isOpen ? <X className="h-5 w-5" data-id="element-895" data-mp="x2ivefu10s38" /> : <Menu className="h-5 w-5" data-id="element-896" data-mp="s0a0hdxlffyh" />}
              <span className="text-sm font-medium tracking-wide" data-id="element-897" data-mp="n28fuokir8kw">
                {isOpen ? 'Stäng' : 'Meny'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {isOpen && <div className={`md:hidden border-t animate-in slide-in-from-top-2 duration-200 ${t.panel}`} data-id="element-898" data-mp="dc5ostwyk4p7">
          <div className="px-4 pt-3 pb-4 space-y-1" data-id="element-899" data-mp="9bv9gqna01xo">
            <p className={`text-xs uppercase tracking-wider font-medium px-3 pb-2 ${t.label}`} data-id="element-900" data-mp="990q72g00n5u">
              Beställ & Tjänster
            </p>
            {links.map(link => <button key={link.type} onClick={() => handleLinkClick(link.type)} className={`flex items-center gap-2 w-full text-left px-3 py-3 rounded-lg text-base font-medium transition-colors ${t.mobileLink}`} data-id="element-901" data-mp="2azzs1891g9e3">
                {link.label}
              </button>)}
            <div className={`h-px my-2 ${t.divider}`} data-id="element-902" data-mp="2oyr0of54t2uu" />
            <p className={`text-xs uppercase tracking-wider font-medium px-3 pb-2 ${t.label}`} data-id="element-903" data-mp="1s9i5jlesua16">
              Besök oss
            </p>
            <div className="px-3 py-3" data-id="element-904" data-mp="1x8uovw6goyz0">
              <HalloweekLink isActive={isEventsActive} dark={dark} onClick={handleEventsClick} size="lg" data-id="element-905" data-mp="21agac5x2alkt" />
            </div>
            <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)} className={`flex items-center gap-2 w-full text-left px-3 py-3 rounded-lg text-base font-medium transition-colors ${t.mobileLink}`} data-id="element-906" data-mp="19ja2t4f0hlli">
              <MapPin className="w-5 h-5" data-id="element-907" data-mp="2bveu8317ke8q" />
              Öppettider & Hitta hit
            </a>
            <div className={`h-px my-2 ${t.divider}`} data-id="element-908" data-mp="2qkaw1ico8v0w" />
            <p className={`text-xs uppercase tracking-wider font-medium px-3 pb-2 ${t.label}`} data-id="element-909" data-mp="2lm9ex2yt35s2">
              Karriär
            </p>
            <button onClick={handleJobsClick} className={`flex items-center gap-2 w-full text-left px-3 py-3 rounded-lg text-base font-medium transition-colors ${t.mobileLink}`} data-id="element-910" data-mp="1xx6z80nqfn07">
              <Briefcase className="w-5 h-5" data-id="element-911" data-mp="26v8x47c65zj9" />
              Jobba hos oss
            </button>
          </div>
        </div>}
    </nav>;
}